import { useState, useCallback, useRef, useEffect, Children } from 'react';
import type { CSSProperties } from 'react';
import type { SDUIComponentProps } from '../../renderer/ComponentRenderer';
import { cn } from '@/lib/utils';
import {
  Collapsible as CollapsiblePrimitive,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible';
import { ChevronDown, X } from 'lucide-react';

/** Flex indicator keys in the style object — only unambiguous flex-intent properties. */
const FLEX_STYLE_INDICATORS = new Set(['flexDirection', 'flexWrap']);

/** Grid indicator keys in the style object. */
const GRID_STYLE_INDICATORS = new Set([
  'gridTemplateColumns',
  'gridTemplateRows',
  'gridTemplateAreas',
  'gridAutoColumns',
  'gridAutoRows',
  'gridAutoFlow',
]);

/**
 * Infers the display mode from CSS properties in the style object.
 */
export function inferDisplayFromStyle(
  style: Record<string, unknown> | undefined,
): 'flex' | 'grid' | undefined {
  if (!style) return undefined;
  const keys = Object.keys(style);

  if (keys.some((k) => GRID_STYLE_INDICATORS.has(k))) return 'grid';
  if (keys.some((k) => FLEX_STYLE_INDICATORS.has(k))) return 'flex';

  return undefined;
}

/**
 * Container component - a smart div that infers display mode from style.
 * CSS properties go in `style`, behavioral props go in `props`.
 *
 * Auto-detects display mode from style:
 * - If flex style (flexDirection, flexWrap) → display: flex
 * - If grid style (gridTemplateColumns, etc.) → display: grid
 * - Otherwise, no display is set (normal block behavior)
 */
export function Container({ id, props, style, children }: SDUIComponentProps) {
  const { className } = props as {
    className?: string;
  };
  const inferredDisplay = inferDisplayFromStyle(style as Record<string, unknown>);

  return (
    <div
      id={id}
      className={className}
      style={{
        ...(inferredDisplay && { display: inferredDisplay }),
        ...(style as CSSProperties),
      }}
    >
      {children}
    </div>
  );
}

/**
 * Card component - a styled container with optional title.
 * Provides default card styling (border, shadow, background).
 */
export function Card({ id, props, style, children }: SDUIComponentProps) {
  const { title, className } = props as {
    title?: string;
    className?: string;
  };

  return (
    <div
      id={id}
      className={cn(
        'bg-card text-card-foreground rounded-lg border border-border shadow-sm',
        className,
      )}
      style={style as CSSProperties}
    >
      {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
      {children}
    </div>
  );
}

/**
 * Grid component - a div with display: grid by default.
 * Use standard CSS grid properties in `style` (gridTemplateColumns, gap, etc.).
 */
export function Grid({ id, props, style, children }: SDUIComponentProps) {
  const { className } = props as {
    className?: string;
  };

  return (
    <div
      id={id}
      className={cn('grid', className)}
      style={{ display: 'grid', ...(style as CSSProperties) }}
    >
      {children}
    </div>
  );
}

/**
 * Flex component - a div with display: flex by default.
 * Use standard CSS flex properties in `style` (flexDirection, justifyContent, etc.).
 */
export function Flex({ id, props, style, children }: SDUIComponentProps) {
  const { className } = props as {
    className?: string;
  };

  return (
    <div
      id={id}
      className={cn('flex', className)}
      style={{ display: 'flex', ...(style as CSSProperties) }}
    >
      {children}
    </div>
  );
}

/**
 * Spacer component - creates empty space.
 * Use width/height in `style` to control size.
 */
export function Spacer({ id, props, style }: SDUIComponentProps) {
  const { className } = props as {
    className?: string;
  };

  return <div id={id} className={className} style={style as CSSProperties} aria-hidden="true" />;
}

/**
 * Divider component - a horizontal or vertical line separator.
 */
export function Divider({ id, props, style }: SDUIComponentProps) {
  const { orientation = 'horizontal', className } = props as {
    orientation?: 'horizontal' | 'vertical';
    className?: string;
  };

  const isVertical = orientation === 'vertical';

  return (
    <div
      id={id}
      role="separator"
      aria-orientation={orientation}
      className={cn('bg-border', isVertical ? 'w-px h-full' : 'h-px w-full', className)}
      style={style as CSSProperties}
    />
  );
}

/**
 * FormGroup component - wraps form inputs with label and error message.
 */
export function FormGroup({ id, props, style, children }: SDUIComponentProps) {
  const { label, required, error, className } = props as {
    label?: string;
    required?: boolean;
    error?: string;
    className?: string;
  };

  return (
    <div id={id} className={cn('mb-4', className)} style={style as CSSProperties}>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-1">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      {children}
      {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
    </div>
  );
}

/**
 * Collapsible component - expandable/collapsible section with header.
 */
export function Collapsible({ id, props, style, children }: SDUIComponentProps) {
  const {
    title,
    defaultOpen,
    disabled,
    variant = 'default',
    className,
  } = props as {
    title: string;
    defaultOpen?: boolean;
    disabled?: boolean;
    variant?: 'default' | 'ghost' | 'bordered';
    className?: string;
  };

  const [isOpen, setIsOpen] = useState(defaultOpen ?? false);

  const variantClasses = {
    default: 'bg-card rounded-lg',
    ghost: 'bg-transparent',
    bordered: 'border border-border rounded-lg',
  };

  return (
    <CollapsiblePrimitive
      id={id}
      open={isOpen}
      onOpenChange={setIsOpen}
      disabled={disabled}
      className={cn(variantClasses[variant], className)}
      style={style as CSSProperties}
    >
      <CollapsibleTrigger
        className={cn(
          'flex items-center justify-between w-full p-4 text-left font-medium transition-colors',
          'hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          disabled && 'opacity-50 cursor-not-allowed',
          variant === 'bordered' && 'rounded-t-lg',
          variant === 'default' && 'rounded-lg',
        )}
        disabled={disabled}
      >
        <span>{title}</span>
        <ChevronDown
          className={cn('h-4 w-4 transition-transform duration-200', isOpen && 'rotate-180')}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-4">{children}</CollapsibleContent>
    </CollapsiblePrimitive>
  );
}

/**
 * Accordion component - group of mutually exclusive collapsibles.
 */
export function Accordion({ id, props, style, children }: SDUIComponentProps) {
  const {
    items = [],
    defaultValue,
    collapsible = true,
    variant = 'default',
    className,
  } = props as {
    items: Array<{ id: string; title: string; disabled?: boolean }>;
    defaultValue?: string;
    collapsible?: boolean;
    variant?: 'default' | 'separated' | 'bordered';
    className?: string;
  };

  const [openItem, setOpenItem] = useState<string | undefined>(defaultValue);

  const handleItemClick = useCallback(
    (itemId: string) => {
      setOpenItem((prev) => {
        if (prev === itemId && collapsible) {
          return undefined;
        }
        return itemId;
      });
    },
    [collapsible],
  );

  const variantClasses = {
    default: '',
    separated: 'space-y-2',
    bordered: 'border border-border rounded-lg divide-y divide-border',
  };

  const itemClasses = {
    default: 'border-b border-border last:border-b-0',
    separated: 'border border-border rounded-lg',
    bordered: '',
  };

  const childArray = Children.toArray(children);

  return (
    <div id={id} className={cn(variantClasses[variant], className)} style={style as CSSProperties}>
      {items.map((item, index) => {
        const isOpen = openItem === item.id;
        const child = childArray[index];

        return (
          <div key={item.id} className={itemClasses[variant]}>
            <button
              type="button"
              onClick={() => !item.disabled && handleItemClick(item.id)}
              disabled={item.disabled}
              className={cn(
                'flex items-center justify-between w-full p-4 text-left font-medium transition-colors',
                'hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-inset',
                item.disabled && 'opacity-50 cursor-not-allowed',
              )}
              aria-expanded={isOpen}
            >
              <span>{item.title}</span>
              <ChevronDown
                className={cn('h-4 w-4 transition-transform duration-200', isOpen && 'rotate-180')}
              />
            </button>
            {isOpen && <div className="px-4 pb-4">{child}</div>}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Panel component - configurable panel with header, close button, collapse.
 */
export function Panel({ id, props, style, children }: SDUIComponentProps) {
  const {
    title,
    subtitle,
    closable,
    collapsible,
    defaultCollapsed,
    variant = 'default',
    className,
  } = props as {
    title?: string;
    subtitle?: string;
    closable?: boolean;
    collapsible?: boolean;
    defaultCollapsed?: boolean;
    variant?: 'default' | 'elevated' | 'outlined' | 'filled';
    className?: string;
  };

  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed ?? false);
  const [isClosed, setIsClosed] = useState(false);

  if (isClosed) {
    return null;
  }

  const variantClasses = {
    default: 'bg-card border border-border rounded-lg',
    elevated: 'bg-card rounded-lg shadow-lg',
    outlined: 'bg-transparent border-2 border-border rounded-lg',
    filled: 'bg-muted rounded-lg',
  };

  const hasHeader = title || closable || collapsible;

  return (
    <div id={id} className={cn(variantClasses[variant], className)} style={style as CSSProperties}>
      {hasHeader && (
        <div
          className={cn(
            'flex items-center justify-between p-4',
            !isCollapsed && 'border-b border-border',
          )}
        >
          <div className="flex-1">
            {title && <h3 className="font-semibold text-foreground">{title}</h3>}
            {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            {collapsible && (
              <button
                type="button"
                onClick={() => setIsCollapsed((prev) => !prev)}
                className="p-1 rounded hover:bg-muted transition-colors"
                aria-label={isCollapsed ? 'Expand' : 'Collapse'}
              >
                <ChevronDown
                  className={cn(
                    'h-4 w-4 transition-transform duration-200',
                    isCollapsed && '-rotate-90',
                  )}
                />
              </button>
            )}
            {closable && (
              <button
                type="button"
                onClick={() => setIsClosed(true)}
                className="p-1 rounded hover:bg-muted transition-colors"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      )}
      {!isCollapsed && <div className="p-4">{children}</div>}
    </div>
  );
}

/**
 * SplitPane component - resizable multi-panel layout.
 */
export function SplitPane({ id, props, style, children }: SDUIComponentProps) {
  const {
    direction = 'horizontal',
    panes = [],
    gutterSize = 8,
    minPaneSize = 10,
    className,
  } = props as {
    direction?: 'horizontal' | 'vertical';
    panes: Array<{
      id: string;
      minSize?: number;
      maxSize?: number;
      defaultSize?: number;
    }>;
    gutterSize?: number;
    minPaneSize?: number;
    className?: string;
  };

  const getInitialSizes = useCallback(() => {
    const total = panes.reduce((sum, p) => sum + (p.defaultSize || 0), 0);
    if (total > 0) {
      return panes.map((p) => p.defaultSize || 100 / panes.length);
    }
    return panes.map(() => 100 / panes.length);
  }, [panes]);

  const [sizes, setSizes] = useState<number[]>(getInitialSizes);
  const containerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<{ index: number; startPos: number; startSizes: number[] } | null>(
    null,
  );

  useEffect(() => {
    setSizes(getInitialSizes());
  }, [panes.length, getInitialSizes]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, gutterIndex: number) => {
      e.preventDefault();
      const startPos = direction === 'horizontal' ? e.clientX : e.clientY;
      draggingRef.current = {
        index: gutterIndex,
        startPos,
        startSizes: [...sizes],
      };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!draggingRef.current || !containerRef.current) return;

        const { index, startPos, startSizes } = draggingRef.current;
        const currentPos = direction === 'horizontal' ? moveEvent.clientX : moveEvent.clientY;
        const containerSize =
          direction === 'horizontal'
            ? containerRef.current.offsetWidth
            : containerRef.current.offsetHeight;

        const delta = ((currentPos - startPos) / containerSize) * 100;

        const newSizes = [...startSizes];
        const pane1 = panes[index];
        const pane2 = panes[index + 1];

        let newSize1 = startSizes[index] + delta;
        let newSize2 = startSizes[index + 1] - delta;

        const min1 = pane1?.minSize ?? minPaneSize;
        const max1 = pane1?.maxSize ?? 100;
        const min2 = pane2?.minSize ?? minPaneSize;
        const max2 = pane2?.maxSize ?? 100;

        if (newSize1 < min1) {
          newSize2 += newSize1 - min1;
          newSize1 = min1;
        } else if (newSize1 > max1) {
          newSize2 += newSize1 - max1;
          newSize1 = max1;
        }

        if (newSize2 < min2) {
          newSize1 += newSize2 - min2;
          newSize2 = min2;
        } else if (newSize2 > max2) {
          newSize1 += newSize2 - max2;
          newSize2 = max2;
        }

        newSizes[index] = newSize1;
        newSizes[index + 1] = newSize2;

        setSizes(newSizes);
      };

      const handleMouseUp = () => {
        draggingRef.current = null;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [direction, sizes, panes, minPaneSize],
  );

  const childArray = Children.toArray(children);

  return (
    <div
      id={id}
      ref={containerRef}
      className={cn(
        'flex',
        direction === 'horizontal' ? 'flex-row' : 'flex-col',
        'h-full w-full',
        className,
      )}
      style={style as CSSProperties}
    >
      {panes.map((pane, index) => (
        <div key={pane.id} className="contents">
          <div
            style={{
              [direction === 'horizontal' ? 'width' : 'height']:
                `calc(${sizes[index]}% - ${(gutterSize * (panes.length - 1)) / panes.length}px)`,
              minWidth: direction === 'horizontal' ? `${pane.minSize ?? minPaneSize}%` : undefined,
              minHeight: direction === 'vertical' ? `${pane.minSize ?? minPaneSize}%` : undefined,
            }}
            className="overflow-auto"
          >
            {childArray[index]}
          </div>
          {index < panes.length - 1 && (
            <div
              role="separator"
              aria-orientation={direction}
              onMouseDown={(e) => handleMouseDown(e, index)}
              style={{
                [direction === 'horizontal' ? 'width' : 'height']: `${gutterSize}px`,
              }}
              className={cn(
                'flex-shrink-0 bg-border hover:bg-primary/50 transition-colors',
                direction === 'horizontal' ? 'cursor-col-resize' : 'cursor-row-resize',
              )}
            />
          )}
        </div>
      ))}
    </div>
  );
}
